import axiosInstance from '../axiosInstance';
import { getManagerRoutePath } from '../managerRouteUtil';

/**
 * Care Needs Service
 * Routes dynamically based on role_id from localStorage user.role.id
 */

const careNeedsService = {
  /**
   * Get all care needs for a residence
   */
  getCareNeedsByResidence: async (residenceId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/care-needs`);
      return response.data;
    } catch (error) {
      console.error('Error fetching care needs:', error);
      throw error;
    }
  },

  /**
   * Get single care need by ID
   */
  getCareNeedById: async (residenceId, careNeedId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/care-needs/${careNeedId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching care need:', error);
      throw error;
    }
  },

  /**
   * Create new care plan
   */
  createCareNeed: async (residenceId, careNeedData) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.post(
        `/${routePath}/residences/${residenceId}/care-needs`,
        careNeedData
      );
      return response.data;
    } catch (error) {
      console.error('Error creating care need:', error);
      throw error;
    }
  },

  /**
   * Update/Review care plan
   */
  updateCareNeed: async (residenceId, careNeedId, careNeedData) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.put(
        `/${routePath}/residences/${residenceId}/care-needs/${careNeedId}`,
        careNeedData
      );
      return response.data;
    } catch (error) {
      console.error('Error updating care need:', error);
      throw error;
    }
  },

  /**
   * Archive / restore a care plan. Body { reason, baseVersion }.
   */
  archiveCareNeed: async (residenceId, careNeedId, body) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(
      `/${routePath}/residences/${residenceId}/care-needs/${careNeedId}/archive`,
      body
    );
    return response.data;
  },

  restoreCareNeed: async (residenceId, careNeedId, body) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(
      `/${routePath}/residences/${residenceId}/care-needs/${careNeedId}/restore`,
      body
    );
    return response.data;
  },

  /**
   * Throw away unapproved edits (a never-approved plan goes entirely).
   */
  discardDraft: async (residenceId, careNeedId) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.delete(
      `/${routePath}/residences/${residenceId}/care-needs/${careNeedId}/draft`
    );
    return response.data;
  },

  /**
   * Write with Patch: rough notes in, draft plans out. Writes nothing.
   */
  patchDrafts: async (residenceId, notes) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(
      `/${routePath}/residences/${residenceId}/care-needs/patch`,
      { notes }
    );
    return response.data;
  },

  /**
   * Write with Patch for an existing plan: its current content plus notes on
   * what changed in, the revised content out. Writes nothing.
   */
  patchRevise: async (residenceId, careNeedId, notes, current) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(
      `/${routePath}/residences/${residenceId}/care-needs/${careNeedId}/patch`,
      { notes, current }
    );
    return response.data;
  },

  /**
   * People who can be "Responsible for review" on a care plan: everyone with
   * dashboard access to this site. [{ id, name, role }]
   */
  getReviewers: async (residenceId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/residences/${residenceId}/care-needs/reviewers`);
      return response.data;
    } catch (error) {
      console.error('Error fetching care plan reviewers:', error);
      throw error;
    }
  },

  /**
   * Get flat list of all carers for dropdowns
   */
  getCarersList: async () => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.get(`/${routePath}/staff`, {
        params: { limit: 500 },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching carers list:', error);
      throw error;
    }
  },

  /**
   * Delete care need
   */
  deleteCareNeed: async (residenceId, careNeedId) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.delete(
        `/${routePath}/residences/${residenceId}/care-needs/${careNeedId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting care need:', error);
      throw error;
    }
  },
};

export default careNeedsService;
